import { MouseEvent } from 'react';
import { DateRangeOptionId, PaginatedTableColumn, PaginatedTableData } from '@common/components';
import { BasicFilters } from '../components/Automated/shared/BasicFilter/BasicFilter.types';

export enum ReferralStatuses {
  PENDING = 'pending',
  UPCOMING = 'upcoming',
  COMPLETED = 'completed',
  DELIVERED = 'delivered',
  NO_SHOW = 'no_show',
  DECLINED = 'declined',
}

export const COMPLETED_STATUSES = [ReferralStatuses.COMPLETED, ReferralStatuses.DELIVERED] as const;

export const isCompletedStatus = (status?: ReferralStatuses): boolean => {
  return status === ReferralStatuses.COMPLETED || status === ReferralStatuses.DELIVERED;
};

export interface Referral {
  appointmentDate: string;
  appointmentStartTime: string;
  appointmentEndTime: string;
  birthDate: string;
  completionDate: string;
  declinedDate: string;
  firstName: string;
  id: string;
  lastName: string;
  patientId: string;
  phoneNumber: string;
  referralDate: string;
  status: ReferralStatuses;
  timezone: string;
}

export interface ReferralListItem {
  appointmentID: string;
  dateOfBirth: string;
  firstName: string;
  lastName: string;
  patientID: string;
  phoneNumber: string;
  status: ReferralStatuses;
  timeZone?: string;
}

export interface PendingReferral extends ReferralListItem {
  referralDateTime: string;
}

export interface UpcomingReferral extends ReferralListItem {
  appointmentEndTime: string;
  appointmentStartTime: string;
}

export interface CompletedReferral extends ReferralListItem {
  completionDateTime: string;
}

export interface DeclinedReferral extends ReferralListItem {
  declinedDateTime: string;
}

export interface ReferralsParams extends UseQueryReferralsParams {
  selectedDateRange?: DateRangeOptionId;
  status: ReferralStatuses;
}

export interface ReferralsResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  size: number;
}

export interface UseQueryReferralsParams {
  offset?: number;
  limit?: number;
  from?: Date | string | undefined;
  to?: Date | string | undefined;
  q?: string;
  sort?: Record<string, boolean>;
  status: ReferralStatuses;
}

export interface ReferralsQueryParams {
  from?: Date | string | undefined;
  q?: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  status: ReferralStatuses;
  to?: Date | string | undefined;
}

export interface UseReferralsTableValue<T> {
  commonColumns: Array<PaginatedTableColumn<T>>;
  dataTable: PaginatedTableData<T> | undefined;
  pageSize: number;
  handleChangeSorting: (sort: Map<string, boolean>) => void;
  handleChangePage: (offset: number) => void;
  handleFilterChange: ({ query, dateRange }: BasicFilters) => void;
  handleRowClick: (event: MouseEvent<HTMLTableRowElement>, item: T, index: number) => void;
}
